<template>
    <div>
        <Dialog v-model:visible="visible" modal header="Add Comment for Status Change" :style="{ width: '50rem' }" :breakpoints="{ '1199px': '75vw', '575px': '90vw' }" :closable="false">
            <base-editor v-model="comment.comment" :errorMessage="formErrors.comment" placeholder="Add a comment before changing task progress to completed" />
            <template #footer>
                <div class="flex justify-content-between gap-2 mt-3">
                    <div v-if="isFeatureEnabled">
                        <Button v-if="!assuranceLevels" label="Progress to completion" severity="help" size="small" rounded @click="toggleAssuranceLevelDropdown(true)" />
                        <base-dropdown v-else v-model="assuranceLevelStatus" :options="assuranceLevelOptions" optionLabel="displayLabel" optionValue="id" class="w-full md:w-12rem" :filter="false" :showClear="false"  />
                    </div>
                    <div>
                        <Button label="Cancel" class="p-button-danger" @click="cancel" />
                        <Button label="Submit" @click="submit" />
                    </div>
                </div>
            </template>
        </Dialog>
        <loader-dialog v-if="loading" />
    </div>
</template>

<script setup>
import { BaseEditor } from '../../../../../modules/custom-inputs';
import LoaderDialog from '../../../../../components/LoaderDialog.vue';

import { ref, defineEmits, defineProps, onUnmounted, computed } from 'vue';
import { storeToRefs } from 'pinia';
import { commentStore, formErrorStore, taskStore } from '../../../../../store/global-stores';
import { CommentSchema, handleErrors } from '../../../../../utilities/schemas';
import { useConfirm } from 'primevue/useconfirm';
import { getAllAssuranceLevels } from '../../../constants';
import { useAssuranceColors } from '@/composables/useAssuranceColors';
import { showToast } from '@/utilities/toast-handler';
import { boolean } from 'yup';

// emits
const emit = defineEmits(['hide-dialog', 'comment-added', 'cancelled']);

// props
const props = defineProps({
    taskId: {
        type: Number,
    },
    isFeatureEnabled: boolean
});

const confirm = useConfirm();
const { comment } = storeToRefs(commentStore);
const { formErrors } = storeToRefs(formErrorStore);
const { assuranceLevelStatus } = storeToRefs(taskStore);
const { assuranceLabels } = useAssuranceColors();
const assuranceLevelOptions = computed(() =>
    getAllAssuranceLevels().map((level) => ({
        ...level,
        displayLabel: assuranceLabels.value[level.id] || level.label,
    }))
);
// states
const visible = ref(true);
const loading = ref(false);
const assuranceLevels = ref(false);

onUnmounted(() => {
    formErrorStore.clearErrors();
});

const submit = async () => {
    let tempAssurance = assuranceLevelStatus.value;
    if(tempAssurance == null && props.isFeatureEnabled)
    {
        showToast({}, 'Select Progress to completion');
        return true;
    }
    const commentAdded = await validateAndSubmit(CommentSchema);
    if (commentAdded) {
        emit('comment-added');
    }
};

const validateAndSubmit = async (schema) => {
    try {
        loading.value = true;
        comment.value.type = ''

        await schema.validate(comment.value, { abortEarly: false });
        formErrorStore.clearErrors();
        return await addComment();
    } catch (error) {
        if (error.inner && typeof error.inner === 'object') {
            const errors = handleErrors(error);
            formErrorStore.addErrors(errors);
        }
    } finally {
        loading.value = false;
    }
};

const addComment = async () => {
    return await commentStore.addTaskComment(props.taskId);
};

const toggleAssuranceLevelDropdown = (flag) => {
    assuranceLevels.value = flag;
};

const cancel = (event) => {
    return confirm.require({
        target: event.currentTarget,
        header: 'Confirmation',
        message: 'Are you sure you want to cancel the changes?',
        icon: 'pi pi-exclamation-triangle',
        accept: () => {
            emit('cancelled');
        },
    });
};
</script>
